// Keep aligned with /shared/excursionPricing.js (Upsun source.root is site-next only).
export function getMinimumAdults(scheduleOrRow, fallbackValue) {
  const raw =
    scheduleOrRow?.minimum_adults ??
    scheduleOrRow?.minimumAdults ??
    fallbackValue ??
    1;
  const value = Number(raw);
  if (!Number.isFinite(value)) return 1;
  return Math.max(1, Math.floor(value));
}

export function computeOptionTotal(passengers, priceRow) {
  const a = Math.max(0, Number(passengers?.adults) || 0);
  const c = Math.max(0, Number(passengers?.children) || 0);
  const i = Math.max(0, Number(passengers?.infants) || 0);
  const adultRaw = Number(priceRow?.price);
  const childRaw = Number(priceRow?.child_price);
  const infantRaw = Number(priceRow?.infant_price);
  const adultPrice = Number.isFinite(adultRaw) ? adultRaw : 0;
  const childPrice = Number.isFinite(childRaw) ? childRaw : adultPrice;
  const infantPrice = Number.isFinite(infantRaw) ? infantRaw : 0;
  const minimumAdults = getMinimumAdults(priceRow?.schedule ?? priceRow ?? null, priceRow?.minimum_adults);
  const nonInfantPax = a + c;
  if (a < minimumAdults) {
    const baseFloor = minimumAdults * adultPrice;
    if (nonInfantPax <= minimumAdults) return baseFloor;
    const includedChildrenInFloor = Math.max(0, minimumAdults - a);
    const extraChildren = Math.max(0, c - includedChildrenInFloor);
    return baseFloor + extraChildren * childPrice + i * infantPrice;
  }
  return a * adultPrice + c * childPrice + i * infantPrice;
}

export const computeBookingPaxTotal = computeOptionTotal;

export function getPriceBreakdown(passengers, priceRow) {
  const a = Math.max(0, Number(passengers?.adults) || 0);
  const c = Math.max(0, Number(passengers?.children) || 0);
  const i = Math.max(0, Number(passengers?.infants) || 0);
  const adultRaw = Number(priceRow?.price);
  const childRaw = Number(priceRow?.child_price);
  const infantRaw = Number(priceRow?.infant_price);
  const adultPrice = Number.isFinite(adultRaw) ? adultRaw : 0;
  const childPrice = Number.isFinite(childRaw) ? childRaw : adultPrice;
  const infantPrice = Number.isFinite(infantRaw) ? infantRaw : 0;
  const minimumAdults = getMinimumAdults(priceRow?.schedule ?? priceRow ?? null, priceRow?.minimum_adults);
  const nonInfantPax = a + c;
  const lines = [];

  if (a < minimumAdults) {
    lines.push({
      label: 'Adult',
      qty: minimumAdults,
      unitPrice: adultPrice,
      subtotal: minimumAdults * adultPrice,
      requestedQty: a,
      minimumAdults,
      minimumApplied: true,
    });
    if (nonInfantPax > minimumAdults) {
      const includedChildrenInFloor = Math.max(0, minimumAdults - a);
      const extraChildren = Math.max(0, c - includedChildrenInFloor);
      if (extraChildren > 0) {
        lines.push({
          label: 'Child',
          qty: extraChildren,
          unitPrice: childPrice,
          subtotal: extraChildren * childPrice,
        });
      }
      if (i > 0) {
        lines.push({
          label: 'Infant',
          qty: i,
          unitPrice: infantPrice,
          subtotal: i * infantPrice,
        });
      }
    }
    return lines;
  }

  if (a > 0) {
    lines.push({
      label: 'Adult',
      qty: a,
      unitPrice: adultPrice,
      subtotal: a * adultPrice,
      requestedQty: a,
      minimumAdults,
      minimumApplied: false,
    });
  }
  if (c > 0) lines.push({ label: 'Child', qty: c, unitPrice: childPrice, subtotal: c * childPrice });
  if (i > 0) lines.push({ label: 'Infant', qty: i, unitPrice: infantPrice, subtotal: i * infantPrice });
  return lines;
}

export function getGroupPricingMeta(passengers, scheduleOrRow) {
  const requestedAdults = Math.max(0, Number(passengers?.adults) || 0);
  const minimumAdults = getMinimumAdults(scheduleOrRow);
  const billedAdults = requestedAdults < minimumAdults ? minimumAdults : requestedAdults;
  return {
    minimumAdults,
    requestedAdults,
    billedAdults,
    isGroupPricing: minimumAdults > 1,
    minimumApplied: billedAdults > requestedAdults,
  };
}

export function parseOnlineDepositPercent(excursionOrPercent) {
  const raw =
    excursionOrPercent && typeof excursionOrPercent === 'object'
      ? excursionOrPercent.online_deposit_percent
      : excursionOrPercent;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0 || n >= 100) return null;
  return Math.round(n);
}

export function splitOnlineDeposit(total, percentOrExcursion) {
  const percent = parseOnlineDepositPercent(percentOrExcursion);
  const amount = Math.max(0, Number(total) || 0);
  if (!percent) {
    return { online: amount, onSite: 0, percent: null };
  }
  const online = Math.round(((amount * percent) / 100) * 100) / 100;
  const onSite = Math.round((amount - online) * 100) / 100;
  return { online, onSite, percent };
}
